import {
  type AaguidCatalog,
  getPasskeyAuthenticatorName,
} from '@newcore/ui/passkey';

// Mirrors shared.constants.webauthn's rejection reasons, which enrollment
// returns as error_code on a 200 with success=false.
export const WebAuthnRejectionCode = {
  AAGUID_NOT_ALLOWED: 'aaguid_not_allowed',
  AAGUID_UNIDENTIFIED: 'aaguid_unidentified',
  ATTESTATION_UNTRUSTED: 'attestation_untrusted',
  ENTERPRISE_ATTESTATION_REQUIRED: 'enterprise_attestation_required',
  SERIAL_NOT_ALLOWED: 'serial_not_allowed',
  SERIAL_ALREADY_ENROLLED: 'serial_already_enrolled',
} as const;

const AAGUID_PREFIX_LENGTH = 8;

function describeModel(
  aaguid: string | null | undefined,
  catalog: AaguidCatalog | undefined
) {
  if (!aaguid) {
    return 'This security key';
  }

  // An unlisted or not-yet-loaded model still names its AAGUID for support.
  const name = getPasskeyAuthenticatorName(aaguid, catalog);

  return name ?? `This model (${aaguid.slice(0, AAGUID_PREFIX_LENGTH)}…)`;
}

/**
 * Copy for a registration your organization's policy refused.
 *
 * Returns null for anything else, including a generic failure with no code,
 * so callers keep their existing message rather than inventing a reason.
 */
export function webAuthnRejectionMessage(
  errorCode: string | null | undefined,
  aaguid: string | null | undefined,
  catalog: AaguidCatalog | undefined
) {
  switch (errorCode) {
    case WebAuthnRejectionCode.AAGUID_NOT_ALLOWED:
      return `${describeModel(aaguid, catalog)} is not allowed by your organization. Try a different security key or passkey.`;
    case WebAuthnRejectionCode.AAGUID_UNIDENTIFIED:
      return "This authenticator did not identify its model, which your organization does not allow. If your browser asked whether to share the security key's make and model, choose to allow it and try again.";
    case WebAuthnRejectionCode.ATTESTATION_UNTRUSTED:
      return `${describeModel(aaguid, catalog)} could not prove it is genuine, so your organization will not accept it. Try a different security key or passkey.`;
    case WebAuthnRejectionCode.ENTERPRISE_ATTESTATION_REQUIRED:
      return 'Your organization only allows corporate security keys that identify themselves individually. Try a different security key.';
    case WebAuthnRejectionCode.SERIAL_NOT_ALLOWED:
      return "This security key's serial number is not on your organization's allowlist. Try a different security key.";
    case WebAuthnRejectionCode.SERIAL_ALREADY_ENROLLED:
      return 'This security key is already registered to another person in your organization.';
    default:
      return null;
  }
}
